import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import api from '../services/api';
import './useColumnWidths.css';

/* ==========================================================================
   Šířky sloupců tabulky — tažení za okraj hlavičky, uložení k uživateli.

   PROČ PROCENTA. Backend (`user_grid_state`, migrace 048) drží šířky
   v procentech viditelné šířky tabulky, ne v pixelech — účetní pracuje
   na notebooku, někdo jiný na širokém monitoru a pixely by se mezi nimi
   nepřenesly. Součet procent smí přesáhnout 100 %; tabulka je pak širší
   než okno a roluje se vodorovně.

   JAK SE POČÍTÁ VÝCHOZÍ STAV. Dokud si uživatel nic nenastavil, změří se
   tabulka tak, jak si ji vysází prohlížeč sám (`table-layout: auto`),
   a naměřené šířky se stanou výchozími. Vypadá pak přesně jako předtím,
   jen už se dá chytit za okraj.
   ========================================================================== */

/** Pod tohle se sloupec stáhnout nedá — jinak by nešel znovu chytit myší. */
const MIN_PX = 44;

/**
 * @param {string} gridKey      klíč tabulky pro API (`statistics.commissions`)
 * @param {string[]} sloupce    klíče sloupců v pořadí, v jakém jsou v hlavičce
 * @param {object} [minima]     volitelné minimální šířky v px pro první změření
 */
export default function useColumnWidths(gridKey, sloupce, minima = {}) {
  /* Obal se připojí až s tabulkou (prázdná sestava žádnou nemá), a to může
     být dávno po odpovědi serveru — proto callback ref do stavu, ne useRef:
     změření se pak spustí přesně ve chvíli, kdy tabulka opravdu existuje. */
  const [wrapEl, setWrapEl] = useState(null);
  const wrapRef = useCallback((node) => setWrapEl(node), []);

  /** { klic: procento viditelné šířky } — null, dokud nevíme (načítá se / měří). */
  const [sirky, setSirky] = useState(null);
  /** Má uživatel uložené vlastní šířky? (podle toho se nabízí „Výchozí šířky") */
  const [vlastni, setVlastni] = useState(false);
  /** Odpověď serveru už dorazila — teprve pak se smí měřit. */
  const [nacteno, setNacteno] = useState(false);

  const klice = sloupce.join('|');

  /* ------------------------------------------------- načtení uloženého -- */
  useEffect(() => {
    let platne = true;
    setSirky(null);
    setNacteno(false);

    api.getGridState(gridKey)
      .then((res) => {
        if (!platne) return;
        const ulozene = res?.data?.columns;
        // Sloupec mohl přibýt — neúplné nastavení se zahodí a změří znovu,
        // půlka tabulky bez šířky by se rozsypala.
        const kompletni = ulozene && sloupce.every(k => typeof ulozene[k] === 'number');
        if (kompletni) {
          setSirky(sloupce.reduce((acc, k) => ({ ...acc, [k]: ulozene[k] }), {}));
          setVlastni(true);
        }
      })
      .catch(() => { /* nastavení tabulky není nic, kvůli čemu hlásit chybu */ })
      .finally(() => { if (platne) setNacteno(true); });

    return () => { platne = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridKey, klice]);

  /* ----------------------------------------------------------- změření -- */
  /**
   * Změří hlavičku tak, jak ji vysází prohlížeč, a vrátí procenta viditelné
   * šířky. Vrátí `null`, když tabulka zrovna není vidět — tabový systém drží
   * neaktivní obrazovky připojené, ale schované (`display: none`), a ze skrytého
   * obalu o nulové šířce by procenta vyšla v desítkách tisíc.
   */
  const zmer = useCallback(() => {
    if (!wrapEl) return null;

    // Čte se obal, ne `<Table>` — react-bootstrap ref dál neposílá.
    const hlavicky = wrapEl.querySelectorAll('thead th');
    if (hlavicky.length !== sloupce.length) return null;

    const sirkaOkna = wrapEl.clientWidth;
    if (!sirkaOkna || sirkaOkna < MIN_PX) return null;

    const namereno = {};
    sloupce.forEach((klic, i) => {
      const px = Math.max(hlavicky[i].getBoundingClientRect().width, minima[klic] || 0, MIN_PX);
      namereno[klic] = Number(((px / sirkaOkna) * 100).toFixed(3));
    });

    return namereno;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapEl, klice]);

  /* Tabulka otevřená rovnou (nebo v modálu) se změří hned. Tabulka, která se
     připojila na pozadí, zůstane nezměřená — vypadá úplně stejně jako předtím
     a změří se až při prvním zatažení za okraj, kdy je vidět z definice.
     `ResizeObserver` se tu nepoužívá schválně: při přechodu z `display: none`
     na viditelné se v Chrome neozve (ověřeno), takže by tenhle případ stejně
     nepokryl. */
  useLayoutEffect(() => {
    if (!nacteno || sirky !== null) return;
    const namereno = zmer();
    if (namereno) setSirky(namereno);
  }, [nacteno, sirky, zmer]);

  /* ----------------------------------------------------------- uložení -- */
  const uloz = useCallback((hodnoty) => {
    api.saveGridState(gridKey, hodnoty)
      .then(() => setVlastni(true))
      .catch(() => {
        window.dispatchEvent(new CustomEvent('app:toast', {
          detail: { message: 'Šířky sloupců se nepodařilo uložit.', variant: 'warning' },
        }));
      });
  }, [gridKey]);

  const vychozi = useCallback(() => {
    api.resetGridState(gridKey)
      .catch(() => { /* i tak se přeměří, server to dožene příště */ })
      .finally(() => { setVlastni(false); setSirky(null); });
  }, [gridKey]);

  /* ------------------------------------------------------------ tažení -- */
  const taziRef = useRef(null);

  const zacniTazeni = useCallback((klic) => (e) => {
    // Hlavička řadí na klik — tažení za okraj s tím nemá nic společného.
    e.preventDefault();
    e.stopPropagation();
    if (!wrapEl) return;

    // Tabulka připojená na pozadí se nezměřila; teď je vidět, tak se to dožene.
    const vychoziSirky = sirky || zmer();
    if (!vychoziSirky) return;
    if (!sirky) setSirky(vychoziSirky);

    const sirkaOkna = wrapEl.clientWidth || 1;
    taziRef.current = {
      klic,
      startX: e.clientX,
      startPct: vychoziSirky[klic],
      minPct: (MIN_PX / sirkaOkna) * 100,
      sirkaOkna,
      aktualni: vychoziSirky,
    };
    document.body.classList.add('grid-resizing');

    const pohyb = (ev) => {
      const t = taziRef.current;
      if (!t) return;
      const deltaPct = ((ev.clientX - t.startX) / t.sirkaOkna) * 100;
      const nova = Math.max(t.minPct, Number((t.startPct + deltaPct).toFixed(3)));
      t.aktualni = { ...t.aktualni, [t.klic]: nova };
      setSirky(t.aktualni);
    };

    const konec = () => {
      const t = taziRef.current;
      document.removeEventListener('mousemove', pohyb);
      document.removeEventListener('mouseup', konec);
      document.body.classList.remove('grid-resizing');
      taziRef.current = null;
      if (t && t.aktualni[t.klic] !== t.startPct) uloz(t.aktualni);
    };

    document.addEventListener('mousemove', pohyb);
    document.addEventListener('mouseup', konec);
  }, [sirky, uloz, wrapEl, zmer]);

  /* ------------------------------------------------- co dostane tabulka - */
  // Když sloupce dohromady nezaberou celou šířku, tabulka zůstane na 100 %
  // a prohlídka je roztáhne poměrově; když přesáhnou, tabulka se o to rozšíří
  // a vodorovné rolování si řeší obal.
  const soucet = sirky ? sloupce.reduce((s, k) => s + (sirky[k] || 0), 0) : 100;
  const celkem = Math.max(100, soucet);

  const colgroup = sirky
    ? sloupce.map(k => ({ key: k, width: `${((sirky[k] / celkem) * 100).toFixed(4)}%` }))
    : null;

  return {
    wrapRef,
    colgroup,
    tableStyle: sirky ? { tableLayout: 'fixed', width: `${celkem.toFixed(4)}%` } : undefined,
    /** Props pro úchyt na pravém okraji `<th>`. */
    resizerProps: (klic) => ({
      className: 'col-resizer',
      role: 'separator',
      'aria-label': 'Změnit šířku sloupce',
      title: 'Táhnutím změníš šířku sloupce',
      onMouseDown: zacniTazeni(klic),
      onClick: (e) => e.stopPropagation(),
    }),
    maUlozene: vlastni,
    vychozi,
  };
}
